import {Chip, Switch} from "@heroui/react";
import {Icon} from "@iconify/react";
import {Spinner} from "@heroui/spinner";
import {addToast} from "@heroui/toast";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {createFileRoute} from '@tanstack/react-router'
import {type ReactNode, useRef, useState} from "react";
import {apiActionImproved, apiRetrieveImproved} from "../../../api";
import PButton from "../../../components/PButton.tsx";
import Header from "../../../Header.tsx";
import EmailJourneys from "../../../master/EmailJourneys.tsx";

export const Route = createFileRoute('/_app/master/campaigns')({
    component: RouteComponent,
})

type Send = {
    uid: string; name: string; email: string; variant: string;
    sent_at: string | null; opened_at: string | null; clicked_at: string | null;
    open_count: number; click_count: number; failed: string | null;
};
type Pending = {user_uid: string; name: string; email: string; variant: string; event: string | null; subject: string; body: string};
type Skipped = {user_uid: string; name: string; email: string; skipped_at: string | null};
type Counts = {sent: number; opened: number; clicked: number; unsubscribed: number; skipped: number; failed: number; open_rate: number | null; click_rate: number | null};
/** A campaign that only sends while a master switch is on; null for the ones that simply run. */
type CampaignSwitch = {key: string; on: boolean} | null;
/** `type` is the tab it is filed under; `manual` means a master sends it by hand, from its console. */
type CampaignSummary = Counts & {key: string; type: string; manual: boolean; label: string; description: string | null; switch?: CampaignSwitch; pending: number; first_sent_at: string | null; last_sent_at: string | null};
type CampaignStats = {
    campaign: {key: string; type: string; manual: boolean; label: string; description: string | null; switch?: CampaignSwitch};
    stats: Counts & {pending: number};
    by_variant: (Counts & {variant: string | null})[];
    by_day: {day: string; sent: number; opened: number; clicked: number}[];
    /** Why the failures failed, grouped — most common first. */
    failures: {reason: string; count: number; last_at: string | null}[];
    sends: Send[];
};
type FeedbackPerson = {user_uid: string; name: string; email: string; events: number; joined: string | null};
type FeedbackGroup = {sport: string; label: string; pending: number; people: FeedbackPerson[]};
/** `html`, `held_back` and `gap_days` come with a feature email; the founder's note is plain text and has no gap. */
type Feedback = {groups: FeedbackGroup[]; preview: {subject: string; body: string; html?: string}; batch_size: number; skipped: Skipped[]; held_back?: number; gap_days?: number};
type Campaign = {
    /** The tabs, in order. */
    types: {key: string; label: string}[];
    campaigns: CampaignSummary[];
    campaign: string;
    stats: {pending: number; sent: number; opened: number; clicked: number; skipped: number; failed: number};
    pending_preview: Pending[];
    skipped: Skipped[];
    sends: Send[];
    nudge: {
        stats: {pending: number; sent: number; opened: number; clicked: number; failed: number};
        sends: Send[];
    };
    email_log: {id: number; email: string; subject: string | null; at: string | null}[];
};

const VARIANT_LABEL: Record<string, string> = {
    stalled:  'Stalled setup',
    empty:    'Nothing created',
    // Trial warning: whether a claim offer was on the table.
    offer:    'With claim offer',
    generic:  'Plain warning',
    // Trial follow-up stages.
    ended:    'Trial ended (day 0)',
    after_3:  'Day 3 after',
    after_7:  'Day 7 after',
    after_14: 'Day 14 after',
    // Dunning grace days.
    stage_1:  'Grace day 1',
    stage_2:  'Grace day 2',
    stage_3:  'Grace day 3',
    upcoming: 'Renews in 7 days',
    // Welcomes.
    organizer:  'Organizer signup',
    tournament: 'Tournament',
    league:     'League',
    // Founder feedback is split by the sport the organizer runs. The enum misspells pickleball.
    pickelball: 'Pickleball',
    none:       'No sport yet',
    feedback:   'The three questions',
    // The Plus trial email: on signup, or a bulk send (each click its own run id). The pass offer: automatic.
    signup:     'On signup',
    auto:       'Automatic',
};

/** A label we know, else the key tidied up ("american_football" → "American football"). */
const variantLabel = (v: string | null | undefined) => {
    if (!v) return 'no variant';
    if (v.startsWith('bulk:')) return 'Bulk send ' + v.slice(5);
    return VARIANT_LABEL[v] || (v.charAt(0).toUpperCase() + v.slice(1)).split('_').join(' ');
};

function SendsTable({sends}: {sends: Send[]}) {
    return (
        <div className="overflow-x-auto rounded-xl border border-default-200">
            <table className="w-full min-w-[900px] text-sm">
                <thead>
                    <tr className="border-b border-default-200 bg-default-50 text-left text-xs uppercase tracking-wider text-default-500">
                        <th className="px-3 py-2">Recipient</th>
                        <th className="px-3 py-2">Email</th>
                        <th className="px-3 py-2">Variant</th>
                        <th className="px-3 py-2">Sent</th>
                        <th className="px-3 py-2">Opened</th>
                        <th className="px-3 py-2">Clicked</th>
                    </tr>
                </thead>
                <tbody>
                    {sends.map((s) => (
                        <tr key={s.uid} className="border-b border-default-100 last:border-0">
                            <td className="px-3 py-2 font-medium">{s.name || <span className="text-default-300">—</span>}</td>
                            <td className="px-3 py-2 text-default-500">{s.email}</td>
                            <td className="whitespace-nowrap px-3 py-2">
                                <Chip size="sm" variant="flat" color={s.variant === 'stalled' ? 'warning' : 'default'}>
                                    {variantLabel(s.variant)}
                                </Chip>
                            </td>
                            <td className="whitespace-nowrap px-3 py-2 text-default-500">
                                {s.failed
                                    ? <Chip size="sm" color="danger" variant="flat" title={s.failed}>Failed</Chip>
                                    : s.sent_at || '—'}
                            </td>
                            <td className="whitespace-nowrap px-3 py-2">
                                {s.opened_at
                                    ? <Chip size="sm" color="success" variant="flat">{s.opened_at}{s.open_count > 1 ? ` ×${s.open_count}` : ''}</Chip>
                                    : <span className="text-default-300">—</span>}
                            </td>
                            <td className="whitespace-nowrap px-3 py-2">
                                {s.clicked_at
                                    ? <Chip size="sm" color="success" variant="flat">{s.clicked_at}{s.click_count > 1 ? ` ×${s.click_count}` : ''}</Chip>
                                    : <span className="text-default-300">—</span>}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

const rate = (n: number | null) => (n === null ? '—' : `${n}% of sent`);

/**
 * The go-live switch for a campaign that has one. Off, its scheduled run only reports; on, it sends.
 * Turning it ON emails real customers from the next run, so that direction asks first and says how
 * many are waiting; turning it off is instant and asks nothing.
 */
function LiveSwitch({sw, label, pending}: {sw: NonNullable<CampaignSwitch>; label: string; pending: number}) {
    const queryClient = useQueryClient();
    const [on, setOn] = useState(sw.on);
    const [saving, setSaving] = useState(false);

    const flip = async (next: boolean) => {
        if (next && !window.confirm(
            `Turn "${label}" on?\n\nIt will start emailing real organizers at the next scheduled run — ${pending} eligible right now, at most 50 a run, once per club.`,
        )) return;
        setSaving(true);
        setOn(next); // optimistic — the server confirms
        try {
            await apiActionImproved('app-settings-set' as any, {key: sw.key, value: next});
            addToast({title: next ? `${label} is live` : `${label} switched off`, description: next ? 'It sends from the next scheduled run.' : 'Nothing more goes out.', color: next ? 'success' : 'default'});
            queryClient.invalidateQueries({queryKey: ['master-campaigns']});
            queryClient.invalidateQueries({queryKey: ['master-campaign-stats']});
        } catch (e: any) {
            setOn(!next);
            addToast({title: e?.message || 'Could not save the switch', color: 'danger'});
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className={'mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border p-3 ' + (on ? 'border-success-200 bg-success-50 dark:bg-success-900/10' : 'border-default-200 bg-default-50')}
             data-campaign-switch={sw.key}>
            <div>
                <div className="flex items-center gap-2 font-semibold">
                    {on ? 'Live' : 'Switched off'}
                    <Chip size="sm" variant="flat" color={on ? 'success' : 'default'}>{on ? 'Sending' : 'Not sending'}</Chip>
                </div>
                <div className="text-xs text-default-500">
                    {on
                        ? 'The scheduled run sends this email. Switch off to stop it at once.'
                        : 'The scheduled run only reports. Send yourself a sample below, then switch on when you are happy with it.'}
                </div>
            </div>
            <Switch isSelected={on} isDisabled={saving} onValueChange={flip} color="success" aria-label={`Send ${label}`} />
        </div>
    );
}

/**
 * One campaign's numbers, opened by clicking it in the list above. `children` is the send console
 * of a campaign a master sends by hand — it sits under the totals, above the history.
 */
function CampaignDetail({stats, children}: {stats: CampaignStats; children?: ReactNode}) {
    const maxDay = Math.max(1, ...stats.by_day.map((d) => d.sent));
    return (
        <div className="rounded-xl border border-default-200 bg-content1 p-4" data-campaign-detail={stats.campaign.key}>
            <h3 className="text-lg font-semibold">{stats.campaign.label}</h3>
            {stats.campaign.description && <p className="mb-3 text-sm text-default-500">{stats.campaign.description}</p>}
            {stats.campaign.switch && (
                // Keyed on the saved state so a refetch after a flip re-seeds the control.
                <LiveSwitch key={String(stats.campaign.switch.on)} sw={stats.campaign.switch} label={stats.campaign.label} pending={stats.stats.pending} />
            )}

            <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-7">
                <Stat label="Sent" value={stats.stats.sent} />
                <Stat label="Opened" value={stats.stats.opened} sub={rate(stats.stats.open_rate)} />
                <Stat label="Clicked" value={stats.stats.clicked} sub={rate(stats.stats.click_rate)} />
                <Stat label="Unsubscribed" value={stats.stats.unsubscribed} sub="from this email" />
                <Stat label="Still to send" value={stats.stats.pending} tone="primary" sub="eligible now" />
                <Stat label="Excluded" value={stats.stats.skipped} sub="won't be emailed" />
                <Stat label="Failed" value={stats.stats.failed} />
            </div>

            {children}

            <SampleSend campaignKey={stats.campaign.key} />

            {/* Why it is failing, not just how often. A campaign showing "0 sent, 13 failed" is
                useless without this, and the reason was previously only a tooltip on one row of a
                200-row table. */}
            {stats.failures.length > 0 && (
                <div className="mb-4 rounded-xl border border-danger-200 bg-danger-50 p-3 dark:bg-danger-900/15">
                    <div className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-danger">
                        <Icon icon="material-symbols-light:error-outline" className="text-lg" />
                        Why {stats.stats.failed === 1 ? 'it failed' : 'they failed'}
                    </div>
                    <ul className="space-y-1.5">
                        {stats.failures.map((f) => (
                            <li key={f.reason} className="flex items-start gap-2 text-sm">
                                <Chip size="sm" color="danger" variant="flat" className="mt-0.5 shrink-0 tabular-nums">
                                    {f.count}×
                                </Chip>
                                <span className="min-w-0 flex-1 break-words font-mono text-tiny leading-relaxed text-default-700">
                                    {f.reason}
                                </span>
                                {f.last_at && <span className="shrink-0 text-tiny text-default-400">{f.last_at}</span>}
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {stats.by_variant.length > 0 && (
                <div className="mb-4 overflow-x-auto rounded-xl border border-default-200">
                    <table className="w-full min-w-[520px] text-sm">
                        <thead>
                            <tr className="border-b border-default-200 bg-default-50 text-left text-xs uppercase tracking-wider text-default-500">
                                <th className="px-3 py-2">Variant</th>
                                <th className="px-3 py-2 text-right">Sent</th>
                                <th className="px-3 py-2 text-right">Opened</th>
                                <th className="px-3 py-2 text-right">Clicked</th>
                                <th className="px-3 py-2 text-right">Failed</th>
                            </tr>
                        </thead>
                        <tbody>
                            {stats.by_variant.map((v) => (
                                <tr key={v.variant || 'none'} className="border-b border-default-100 last:border-0">
                                    <td className="px-3 py-2">
                                        <Chip size="sm" variant="flat" color={v.variant === 'stalled' ? 'warning' : 'default'}>
                                            {variantLabel(v.variant)}
                                        </Chip>
                                    </td>
                                    <td className="px-3 py-2 text-right tabular-nums">{v.sent}</td>
                                    <td className="px-3 py-2 text-right tabular-nums">{v.opened} <span className="text-xs text-default-400">{v.open_rate === null ? '' : `(${v.open_rate}%)`}</span></td>
                                    <td className="px-3 py-2 text-right tabular-nums">{v.clicked} <span className="text-xs text-default-400">{v.click_rate === null ? '' : `(${v.click_rate}%)`}</span></td>
                                    <td className="px-3 py-2 text-right tabular-nums">{v.failed}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {stats.by_day.length > 0 && (
                <div className="mb-4">
                    <div className="mb-1 text-xs font-semibold uppercase tracking-wider text-default-500">Sent per day, last 30 days</div>
                    <div className="flex h-24 items-end gap-1 rounded-xl border border-default-200 px-3 pt-3 pb-2">
                        {stats.by_day.map((d) => (
                            <div key={d.day} className="flex flex-1 flex-col items-center justify-end" title={`${d.day}: ${d.sent} sent, ${d.opened} opened, ${d.clicked} clicked`}>
                                <div className="w-full rounded-t bg-primary/70" style={{height: `${Math.max(6, (d.sent / maxDay) * 64)}px`}} />
                                <div className="mt-1 text-[10px] text-default-400">{d.day.slice(5)}</div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div className="mb-1 text-xs font-semibold uppercase tracking-wider text-default-500">Recipients (latest {stats.sends.length})</div>
            {stats.sends.length === 0
                ? <div className="rounded-xl border border-default-200 py-8 text-center text-default-500">Nothing sent yet.</div>
                : <SendsTable sends={stats.sends} />}
        </div>
    );
}

/**
 * Send a copy of this campaign to an address, from the campaign itself.
 *
 * Checking a template used to mean SSH and a scratch script. Every variant of the campaign goes
 * out, and a failure reports the mailer error rather than a bare "failed" — when a test send
 * fails, the reason is the whole result.
 */
function SampleSend({campaignKey}: {campaignKey: string}) {
    const [email, setEmail] = useState("");
    const [result, setResult] = useState<{sent: string[]; failed: Record<string, string>} | null>(null);

    const send = useMutation({
        mutationFn: async () => {
            const r: any = await apiActionImproved("master/campaigns/sample-send" as any, {campaign: campaignKey, email});
            if (r?.error) throw new Error(r.error);
            return r as {sent: string[]; failed: Record<string, string>};
        },
        onSuccess: (r) => {
            setResult(r);
            const failedCount = Object.keys(r.failed || {}).length;
            addToast({
                title: failedCount ? failedCount + " of " + ((r.sent?.length || 0) + failedCount) + " failed" : "Sent to " + email,
                color: failedCount ? "danger" : "success",
            });
        },
        onError: (e: any) => addToast({title: e?.message || "Could not send", color: "danger"}),
    });

    // Deliberately loose — the server validates the address properly, and all this needs to do is
    // stop an empty or half-typed box being submitted. It previously read /^[^@s]+@[^@s]+.[^@s]+$/,
    // whose \s escapes had been lost: the classes excluded the LETTER "s", so the button sat
    // silently disabled for every address containing one, with nothing on screen to say why.
    const [local, domain, ...extra] = email.trim().split('@');
    const valid = extra.length === 0 && !!local && !!domain && domain.includes('.') && !email.trim().includes(' ');

    return (
        <div className="mb-4 rounded-xl border border-default-200 bg-default-50 p-3">
            <div className="mb-2 flex items-center gap-1.5 text-sm font-semibold">
                <Icon icon="material-symbols-light:outgoing-mail" className="text-lg text-primary" />
                Send this email to someone
            </div>
            <p className="mb-2 text-tiny text-default-500">
                Every variant of this campaign, to one address. Nothing is recorded against the campaign numbers.
            </p>
            <div className="flex flex-wrap items-center gap-2">
                <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@scorepal.co"
                    className="min-w-[220px] flex-1 rounded-lg border border-default-200 bg-content1 px-3 py-2 text-sm outline-none focus:border-primary"
                />
                <PButton size="sm" color="primary" isDisabled={!valid || send.isPending} isLoading={send.isPending}
                         onPress={() => send.mutate()}>
                    Send
                </PButton>
            </div>
            {result && (
                <div className="mt-2 space-y-1 text-tiny">
                    {(result.sent || []).map((v) => (
                        <div key={v} className="text-success">✓ {v} sent</div>
                    ))}
                    {Object.entries(result.failed || {}).map(([v, reason]) => (
                        <div key={v} className="text-danger">✗ {v} — <span className="font-mono">{reason}</span></div>
                    ))}
                </div>
            )}
        </div>
    );
}

function Stat({label, value, sub, tone}: {label: string; value: number; sub?: string; tone?: 'default' | 'primary'}) {
    return (
        <div className="rounded-xl border border-default-200 bg-content1 px-4 py-3">
            <div className={'text-2xl font-bold tabular-nums ' + (tone === 'primary' ? 'text-primary' : '')}>{value}</div>
            <div className="text-sm font-medium">{label}</div>
            {sub && <div className="text-xs text-default-400">{sub}</div>}
        </div>
    );
}

/**
 * Founder feedback — Shaun's three questions, sent by hand one sport at a time so the replies can be
 * read as a group.
 *
 * "Send to all" is a loop, not one request: the server sends a slice per call and says how many are
 * left, so a group of any size goes out without a request outliving its time limit, the count on
 * screen moves as it goes, and Stop really stops it (after the slice in flight).
 */
function FeedbackConsole({campaign}: {campaign: string}) {
    // The founder's questions have their own endpoints; every feature email shares one pair.
    const isFeature = campaign.startsWith('feature_');
    const dataPath = isFeature ? 'master/campaigns/feature' : 'master/campaigns/feedback';
    const queryClient = useQueryClient();
    const [open, setOpen] = useState<string | null>(null);
    const [run, setRun] = useState<{sport: string; label: string; total: number; sent: number; failed: number} | null>(null);
    const stop = useRef(false);

    const query = useQuery<Feedback>({
        queryKey: ['master-campaign-feedback', campaign],
        queryFn:  () => apiRetrieveImproved(dataPath as any, {campaign}),
    });

    const refresh = () => {
        queryClient.invalidateQueries({queryKey: ['master-campaign-feedback']});
        queryClient.invalidateQueries({queryKey: ['master-campaigns']});
        queryClient.invalidateQueries({queryKey: ['master-campaign-stats']});
    };

    const skip = useMutation({
        mutationFn: async ({userUid, undo}: {userUid: string; undo?: boolean}) =>
            apiActionImproved('master/campaigns/skip' as any, {user_uid: userUid, undo: !!undo, campaign}),
        onSuccess: (_res, vars) => {
            addToast({title: vars.undo ? 'Back in the queue' : "Won't be emailed", color: 'success'});
            refresh();
        },
        onError: () => addToast({title: 'Could not update', color: 'danger'}),
    });

    // `g.sport` null = everyone who is ready, whatever they run.
    const sendAll = async (g: {sport: string | null; label: string; pending: number}) => {
        if (!window.confirm(
            `Send this email to all ${g.pending} ${g.label} ${g.pending === 1 ? 'organizer' : 'organizers'}?\n\n`
            + (isFeature
                ? 'These are real emails. Each person gets each feature email once, and never two within a few days.'
                : 'These are real emails, from you, promising a free Event Pass for a reply. Each person is only ever asked once.'),
        )) return;

        stop.current = false;
        let sent = 0;
        let failed = 0;
        setRun({sport: g.sport || '', label: g.label, total: g.pending, sent, failed});
        try {
            // One slice per call until nobody is left. A slice that moved nobody ends it too, so a
            // server that keeps answering "still 40 to go" can never spin this forever.
            for (;;) {
                const res: any = await apiActionImproved((dataPath + '/send') as any, {campaign, ...(g.sport ? {sport: g.sport} : {})});
                sent += res.sent || 0;
                failed += res.failed || 0;
                setRun({sport: g.sport || '', label: g.label, total: g.pending, sent, failed});
                if (!res.remaining || (res.sent || 0) + (res.failed || 0) === 0 || stop.current) break;
            }
            addToast({
                title:       `${sent} sent to ${g.label ? g.label + ' organizers' : 'organizers'}`,
                description: failed ? `${failed} failed — the reasons are under "Why they failed"` : 'Replies go to support@scorepal.co',
                color:       failed ? 'warning' : 'success',
            });
        } catch (e: any) {
            addToast({title: 'Sending stopped', description: `${sent} went out before it failed: ${e?.message || 'try again'}`, color: 'danger'});
        } finally {
            setRun(null);
            refresh();
        }
    };

    const f = query.data;
    if (!f) return <div className="mb-4 flex justify-center py-6"><Spinner size="sm" /></div>;

    const total = f.groups.reduce((n, g) => n + g.pending, 0);

    return (
        <div className="mb-4" data-feedback-console>
            <div className="mb-3 rounded-xl border border-primary-200 bg-primary-50 p-3 text-sm dark:bg-primary-900/10">
                <div className="flex flex-wrap items-center gap-3">
                    <div className="font-semibold">{total} {total === 1 ? 'organizer' : 'organizers'} {isFeature ? 'ready for this email' : 'still to ask'}, grouped by the sport they run</div>
                    {/* A feature email is for everybody; the founder's questions are read a sport at a time. */}
                    {isFeature && total > 0 && (
                        <PButton size="sm" color="primary" className="ml-auto" isDisabled={!!run}
                                 onPress={() => void sendAll({sport: null, label: '', pending: total})}>
                            Send to everyone ({total})
                        </PButton>
                    )}
                </div>
                <div className="mt-0.5 text-xs text-default-600">
                    Their sport is the one most of their events are in, else what they said in the signup quiz.{' '}
                    {isFeature ? <>
                        Each person gets this email once.
                        {!!f.held_back && <> <span className="font-semibold">{f.held_back} more are waiting</span>: they had another feature email in the last {f.gap_days} days, and become ready by themselves.</>}
                        {' '}Replies land in support@scorepal.co.
                    </> : <>
                        Everyone is asked once, ever. Replies land in support@scorepal.co. The email promises a free Event Pass for
                        answering — give it from Master → Activation → <span className="font-semibold">Give a free Event Pass</span> when their reply arrives.
                    </>}
                </div>
            </div>

            <details className="mb-3 rounded-xl border border-default-200 bg-content1">
                <summary className="cursor-pointer px-4 py-3 text-sm">
                    <span className="text-default-400">The email — subject: </span>
                    <span className="font-medium">{f.preview.subject}</span>
                </summary>
                <div className="border-t border-default-100 px-4 py-3">
                    {f.preview.html ? (
                        // A designed email is shown as it lands, screenshot and all. Sandboxed: it is
                        // our own markup, but nothing in a preview should ever run or navigate.
                        <iframe title="Email preview" sandbox="" srcDoc={f.preview.html}
                                className="h-[640px] w-full rounded-lg border border-default-200 bg-white" />
                    ) : (
                        // Plain text is what actually sends, so show it as plain text.
                        <pre className="max-h-96 overflow-auto whitespace-pre-wrap rounded-lg bg-default-50 p-3 font-sans text-sm leading-relaxed">{f.preview.body}</pre>
                    )}
                </div>
            </details>

            {run && (
                <div className="mb-3 flex flex-wrap items-center gap-3 rounded-xl border border-warning-200 bg-warning-50 p-3 text-sm dark:bg-warning-900/10" data-feedback-progress>
                    <Spinner size="sm" />
                    <span>
                        Sending to {run.label || 'everyone'}: <span className="font-bold tabular-nums">{run.sent + run.failed}</span> of {run.total}
                        {run.failed > 0 && <span className="text-danger"> · {run.failed} failed</span>}
                    </span>
                    <span className="text-xs text-default-500">Keep this tab open until it finishes.</span>
                    <button type="button" className="ml-auto rounded-lg border border-default-300 px-3 py-1 text-xs font-semibold hover:border-danger hover:text-danger"
                            onClick={() => { stop.current = true; }}>
                        Stop after this batch
                    </button>
                </div>
            )}

            {f.groups.length === 0 ? (
                <div className="mb-3 rounded-xl border border-default-200 py-8 text-center text-default-500">Everyone has been asked.</div>
            ) : (
                <div className="mb-3 overflow-x-auto rounded-xl border border-default-200">
                    <table className="w-full min-w-[520px] text-sm">
                        <thead>
                            <tr className="border-b border-default-200 bg-default-50 text-left text-xs uppercase tracking-wider text-default-500">
                                <th className="px-3 py-2">Sport</th>
                                <th className="px-3 py-2 text-right">To send</th>
                                <th className="px-3 py-2"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {f.groups.map((g) => (
                                <FeedbackGroupRows key={g.sport} group={g} isOpen={open === g.sport} busy={!!run}
                                                   onToggle={() => setOpen(open === g.sport ? null : g.sport)}
                                                   onSend={() => void sendAll(g)}
                                                   onSkip={(userUid) => skip.mutate({userUid})} />
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {f.skipped.length > 0 && (
                <details className="rounded-xl border border-default-200 bg-content1 p-4">
                    <summary className="cursor-pointer text-sm font-semibold">Excluded ({f.skipped.length}) — these people won't be asked</summary>
                    <div className="mt-3 flex flex-col gap-1.5">
                        {f.skipped.map((s) => (
                            <div key={s.user_uid} className="flex flex-wrap items-center gap-2 text-sm">
                                <span className="font-medium">{s.name || s.email}</span>
                                <span className="text-default-400">{s.email}</span>
                                <span className="text-xs text-default-400">excluded {s.skipped_at}</span>
                                <button type="button" className="ml-auto rounded-lg border border-default-200 px-2 py-1 text-xs text-default-500 transition hover:border-primary hover:text-primary"
                                        onClick={() => skip.mutate({userUid: s.user_uid, undo: true})}>
                                    Put back
                                </button>
                            </div>
                        ))}
                    </div>
                </details>
            )}
        </div>
    );
}

/** One sport: its row, and — opened — who is in it, each with a way to leave them out. */
function FeedbackGroupRows({group, isOpen, busy, onToggle, onSend, onSkip}: {
    group: FeedbackGroup; isOpen: boolean; busy: boolean; onToggle: () => void; onSend: () => void; onSkip: (userUid: string) => void;
}) {
    return <>
        <tr data-feedback-sport={group.sport} className={'cursor-pointer border-b border-default-100 transition hover:bg-default-50 ' + (isOpen ? 'bg-primary/5' : '')} onClick={onToggle}>
            <td className="px-3 py-2.5 font-medium">
                {group.label}
                <span className="ml-2 text-xs font-normal text-primary">{isOpen ? 'Hide people' : 'See who'}</span>
            </td>
            <td className="px-3 py-2.5 text-right tabular-nums">{group.pending}</td>
            <td className="px-3 py-2 text-right" onClick={(e) => e.stopPropagation()}>
                <PButton size="sm" color="primary" isDisabled={busy || group.pending === 0} onPress={onSend}>
                    Send to all {group.pending}
                </PButton>
            </td>
        </tr>
        {isOpen && (
            <tr className="border-b border-default-100 bg-default-50/60">
                <td colSpan={3} className="px-3 py-2">
                    <div className="flex max-h-72 flex-col gap-1 overflow-y-auto">
                        {group.people.map((p) => (
                            <div key={p.user_uid} className="flex flex-wrap items-center gap-2 text-sm">
                                <span className="font-medium">{p.name || p.email}</span>
                                <span className="text-default-400">{p.email}</span>
                                <span className="text-xs text-default-400">
                                    {p.events > 0 ? `${p.events} ${p.events === 1 ? 'event' : 'events'}` : 'no events'}{p.joined ? ` · joined ${p.joined}` : ''}
                                </span>
                                <button type="button" disabled={busy}
                                        className="ml-auto rounded-lg border border-default-200 px-2 py-0.5 text-xs text-default-500 transition hover:border-danger hover:text-danger disabled:opacity-50"
                                        onClick={() => onSkip(p.user_uid)}>
                                    Don't email
                                </button>
                            </div>
                        ))}
                        {group.pending > group.people.length && (
                            <div className="text-xs text-default-400">Showing the first {group.people.length} of {group.pending}. The rest are sent to as well.</div>
                        )}
                    </div>
                </td>
            </tr>
        )}
    </>;
}

/** Organizer win-back: send the next batch, read exactly what each person gets, leave people out. */
type PitchFilters = {from: string; to: string; country: string; sport: string; path: string; fresh_only: boolean; seen_modal: boolean};
type PitchConsole = {
    enabled: boolean;
    /** The master's own address, for "Send me a test". */
    me: string | null;
    total: number;
    /** Who the exclusions leave out, counted by reason. */
    skipped: Record<string, number>;
    people: {club_uid: string; club: string | null; name: string; email: string; country: string | null; path: string | null; joined: string | null}[];
    preview: {subject: string; html: string} | null;
    batch_size: number;
    sports: string[];
};
const SKIP_LABEL: Record<string, string> = {
    already_sent:  'already had it',
    legacy:        'legacy club',
    on_plan:       'on Plus or a pass',
    trial_used:    'trial started or used',
    sign_in_as:    'made in a sign-in-as session',
    no_email:      'no email address',
    unsubscribed:  'unsubscribed',
    staff_or_test: 'staff or test account',
    has_pass:      'bought an Event Pass',
    // The pass offer's own.
    already_offered: 'already offered',
    trial_started:   'Plus trial started',
    no_event:        'no open above-cap event',
};

/**
 * The Plus trial email, by hand: filter the audience (signed up between dates, country, sport,
 * organiser path, "no pass / no Plus / no trial" on by default), watch the count move, read the
 * email as it lands, send yourself a copy, then send in batches. The same exclusions and the
 * once-per-club rule as the signup send, so a club that already had it on signup is skipped and
 * counted here as such.
 */
function AudienceConsole({campaign, dataPath, sendPath, seenModal, seenLabel, blurb}: {campaign: string; dataPath: string; sendPath: string; seenModal?: boolean; seenLabel?: string; blurb: string}) {
    const queryClient = useQueryClient();
    const [filters, setFilters] = useState<PitchFilters>({from: '', to: '', country: '', sport: '', path: '', fresh_only: true, seen_modal: true});
    const [run, setRun] = useState<{total: number; sent: number; failed: number} | null>(null);
    const stop = useRef(false);

    // Only the filters that are set go over, so the server's defaults hold for the rest.
    const params = () => ({
        ...(filters.from ? {from: filters.from} : {}),
        ...(filters.to ? {to: filters.to} : {}),
        ...(filters.country.trim().length === 2 ? {country: filters.country.trim().toUpperCase()} : {}),
        ...(filters.sport ? {sport: filters.sport} : {}),
        ...(filters.path ? {path: filters.path} : {}),
        fresh_only: filters.fresh_only,
        ...(seenModal ? {seen_modal: filters.seen_modal} : {}),
    });

    const query = useQuery<PitchConsole>({
        queryKey: ['master-campaign-audience', campaign, filters],
        queryFn:  () => apiRetrieveImproved(dataPath as any, params() as any),
    });

    const refresh = () => {
        queryClient.invalidateQueries({queryKey: ['master-campaign-audience']});
        queryClient.invalidateQueries({queryKey: ['master-campaigns']});
        queryClient.invalidateQueries({queryKey: ['master-campaign-stats']});
    };

    const testSend = useMutation({
        mutationFn: async () => {
            const r: any = await apiActionImproved('master/campaigns/sample-send' as any, {campaign, email: query.data?.me});
            if (r?.error) throw new Error(r.error);
            if (Object.keys(r?.failed || {}).length) throw new Error(String(Object.values(r.failed)[0]));
            return r;
        },
        onSuccess: () => addToast({title: `Sent to ${query.data?.me}`, description: 'A test copy; nothing is recorded.', color: 'success'}),
        onError:   (e: any) => addToast({title: e?.message || 'Could not send the test', color: 'danger'}),
    });

    const sendAll = async (total: number) => {
        if (!window.confirm(`Send this email to all ${total} ${total === 1 ? 'club' : 'clubs'} the filters give?\n\nThese are real emails. Each club only ever gets this once; the automatic send and this one share that rule.`)) return;
        stop.current = false;
        // One run id per click, so the log can tell this send from the next.
        const runId = Date.now().toString(36);
        let sent = 0;
        let failed = 0;
        setRun({total, sent, failed});
        try {
            for (;;) {
                const res: any = await apiActionImproved(sendPath as any, {...params(), run: runId});
                sent += res.sent || 0;
                failed += res.failed || 0;
                setRun({total, sent, failed});
                if (!res.remaining || (res.sent || 0) + (res.failed || 0) === 0 || stop.current) break;
            }
            addToast({title: `${sent} sent`, description: failed ? `${failed} failed — the reasons are under "Why they failed"` : 'Replies go to support@scorepal.co', color: failed ? 'warning' : 'success'});
        } catch (e: any) {
            addToast({title: 'Sending stopped', description: `${sent} went out before it failed: ${e?.message || 'try again'}`, color: 'danger'});
        } finally {
            setRun(null);
            refresh();
        }
    };

    const d = query.data;
    const field = 'rounded-lg border border-default-200 bg-content1 px-2 py-1.5 text-sm outline-none focus:border-primary';
    const skipped = Object.entries(d?.skipped || {}).sort((a, b) => b[1] - a[1]);

    return (
        <div className="mb-4" data-plus-trial-console>
            <div className="mb-3 rounded-xl border border-primary-200 bg-primary-50 p-3 text-sm dark:bg-primary-900/10">
                <div className="mb-2 font-semibold">Who gets it</div>
                <div className="flex flex-wrap items-end gap-3">
                    <label className="text-xs text-default-500">Signed up from<br /><input type="date" value={filters.from} max={filters.to || undefined} onChange={(e) => setFilters({...filters, from: e.target.value})} className={field} /></label>
                    <label className="text-xs text-default-500">to<br /><input type="date" value={filters.to} min={filters.from || undefined} onChange={(e) => setFilters({...filters, to: e.target.value})} className={field} /></label>
                    <label className="text-xs text-default-500">Country<br /><input value={filters.country} maxLength={2} placeholder="GB" onChange={(e) => setFilters({...filters, country: e.target.value})} className={field + ' w-16 uppercase'} /></label>
                    <label className="text-xs text-default-500">Sport<br />
                        <select value={filters.sport} onChange={(e) => setFilters({...filters, sport: e.target.value})} className={field}>
                            <option value="">Any</option>
                            {(d?.sports || []).map((s) => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1).split('_').join(' ')}</option>)}
                        </select>
                    </label>
                    <label className="text-xs text-default-500">Organiser path<br />
                        <select value={filters.path} onChange={(e) => setFilters({...filters, path: e.target.value})} className={field}>
                            <option value="">Any</option>
                            <option value="one_off">One-off</option>
                            <option value="community">Community</option>
                        </select>
                    </label>
                    <label className="flex items-center gap-1.5 pb-1.5 text-xs text-default-600">
                        <input type="checkbox" checked={filters.fresh_only} onChange={(e) => setFilters({...filters, fresh_only: e.target.checked})} />
                        No pass / no Plus / no trial
                    </label>
                    {seenModal && (
                        <label className="flex items-center gap-1.5 pb-1.5 text-xs text-default-600">
                            <input type="checkbox" checked={filters.seen_modal} onChange={(e) => setFilters({...filters, seen_modal: e.target.checked})} />
                            {seenLabel || 'Has seen the pass modal'}
                        </label>
                    )}
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-3">
                    {query.isFetching && !d ? <Spinner size="sm" /> : (
                        <div className="text-base"><span className="text-2xl font-bold tabular-nums" data-plus-trial-count>{d?.total ?? 0}</span> {d?.total === 1 ? 'club' : 'clubs'} would get it{query.isFetching ? ' …' : ''}</div>
                    )}
                    {skipped.length > 0 && (
                        <div className="text-xs text-default-500">
                            Left out: {skipped.map(([k, n]) => `${n} ${SKIP_LABEL[k] || k}`).join(' · ')}
                        </div>
                    )}
                    <div className="ml-auto flex items-center gap-2">
                        <PButton size="sm" variant="flat" isDisabled={!d?.me || testSend.isPending || !!run} isLoading={testSend.isPending} onPress={() => testSend.mutate()}>
                            Send me a test{d?.me ? ` (${d.me})` : ''}
                        </PButton>
                        <PButton size="sm" color="primary" isDisabled={!d || d.total === 0 || !!run || !d.enabled} onPress={() => void sendAll(d?.total || 0)}>
                            Send to {d?.total ?? 0}
                        </PButton>
                    </div>
                </div>
                <div className="mt-1 text-xs text-default-500">
                    {blurb} Sent in batches of {d?.batch_size ?? 25}; keep the tab open.
                </div>
            </div>

            {d?.preview && (
                <details className="mb-3 rounded-xl border border-default-200 bg-content1">
                    <summary className="cursor-pointer px-4 py-3 text-sm">
                        <span className="text-default-400">The email — subject: </span>
                        <span className="font-medium">{d.preview.subject}</span>
                        <span className="text-default-400"> (rendered for a sample club)</span>
                    </summary>
                    <div className="border-t border-default-100 px-4 py-3">
                        <iframe title="Email preview" sandbox="" srcDoc={d.preview.html} className="h-[720px] w-full rounded-lg border border-default-200 bg-white" />
                    </div>
                </details>
            )}

            {run && (
                <div className="mb-3 flex flex-wrap items-center gap-3 rounded-xl border border-warning-200 bg-warning-50 p-3 text-sm dark:bg-warning-900/10" data-plus-trial-progress>
                    <Spinner size="sm" />
                    <span>Sending: <span className="font-bold tabular-nums">{run.sent + run.failed}</span> of {run.total}{run.failed > 0 && <span className="text-danger"> · {run.failed} failed</span>}</span>
                    <button type="button" className="ml-auto rounded-lg border border-default-300 px-3 py-1 text-xs font-semibold hover:border-danger hover:text-danger" onClick={() => { stop.current = true; }}>
                        Stop after this batch
                    </button>
                </div>
            )}

            {d && d.people.length > 0 && (
                <div className="mb-3 overflow-x-auto rounded-xl border border-default-200">
                    <table className="w-full min-w-[640px] text-sm">
                        <thead>
                            <tr className="border-b border-default-200 bg-default-50 text-left text-xs uppercase tracking-wider text-default-500">
                                <th className="px-3 py-2">Club</th>
                                <th className="px-3 py-2">Organiser</th>
                                <th className="px-3 py-2">Country</th>
                                <th className="px-3 py-2">Path</th>
                                <th className="px-3 py-2">Signed up</th>
                            </tr>
                        </thead>
                        <tbody>
                            {d.people.map((p) => (
                                <tr key={p.club_uid} className="border-b border-default-100 last:border-0">
                                    <td className="px-3 py-1.5 font-medium">{p.club || p.club_uid}</td>
                                    <td className="px-3 py-1.5">{p.name || p.email} <span className="text-xs text-default-400">{p.email}</span></td>
                                    <td className="px-3 py-1.5">{p.country || '—'}</td>
                                    <td className="px-3 py-1.5">{p.path === 'one_off' ? 'One-off' : p.path === 'community' ? 'Community' : '—'}</td>
                                    <td className="px-3 py-1.5 text-default-500">{p.joined || '—'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {d.total > d.people.length && <div className="px-3 py-2 text-xs text-default-400">The first {d.people.length} of {d.total}.</div>}
                </div>
            )}
        </div>
    );
}

function WinbackConsole({c}: {c: Campaign}) {
    const queryClient = useQueryClient();
    const [batch, setBatch] = useState(10);

    const refresh = () => {
        queryClient.invalidateQueries({queryKey: ['master-campaigns']});
        queryClient.invalidateQueries({queryKey: ['master-campaign-stats']});
    };

    const sendBatch = useMutation({
        mutationFn: async () => apiActionImproved('master/campaigns/send' as any, {limit: batch}),
        onSuccess:  (res: any) => {
            addToast({
                title:       `${res.sent} sent`,
                description: res.failed ? `${res.failed} failed — the reasons are below` : 'Replies go to support@scorepal.co',
                color:       res.failed ? 'warning' : 'success',
            });
            refresh();
        },
        onError: (e: any) => addToast({title: 'Send failed', description: e?.response?.data?.error || 'Try again', color: 'danger'}),
    });

    const skip = useMutation({
        mutationFn: async ({userUid, undo}: {userUid: string; undo?: boolean}) =>
            apiActionImproved('master/campaigns/skip' as any, {user_uid: userUid, undo: !!undo}),
        onSuccess: (_res, vars) => {
            addToast({title: vars.undo ? 'Back in the queue' : "Won't be emailed", color: 'success'});
            refresh();
        },
        onError: () => addToast({title: 'Could not update', color: 'danger'}),
    });

    return (
        <div className="mb-4" data-winback-console>
            <p className="mb-3 text-xs text-default-400">
                Each person is emailed once; replies go to support@scorepal.co. Opens are indicative only: Apple Mail and Gmail
                pre-fetch images, which inflates the number, while image blocking hides real opens. Clicks and replies are the
                trustworthy signals.
            </p>

            {/* Sending is real email to real people, so the batch size is explicit and capped. */}
            <div className="mb-4 flex flex-wrap items-center gap-3 rounded-xl border border-default-200 bg-default-50 p-4">
                <span className="text-sm">Send the next</span>
                <select
                    aria-label="Batch size"
                    className="rounded-lg border border-default-200 bg-content1 px-2 py-1 text-sm"
                    value={batch}
                    onChange={(e) => setBatch(Number(e.target.value))}
                >
                    {[5, 10, 25, 50].map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
                <span className="text-sm">of {c.stats.pending}</span>
                <PButton
                    color="primary"
                    isDisabled={c.stats.pending === 0}
                    isLoading={sendBatch.isPending}
                    onPress={() => {
                        const n = Math.min(batch, c.stats.pending);
                        if (window.confirm(`Send ${n} real ${n === 1 ? 'email' : 'emails'} now? Each one promises a personal reply.`)) {
                            sendBatch.mutate();
                        }
                    }}
                >
                    Send batch
                </PButton>
                <span className="text-xs text-default-400">Or run <code>php artisan organizers:winback --dry-run</code></span>
            </div>

            {c.pending_preview.length > 0 && (
                <div className="mb-4">
                    <div className="mb-1 text-sm font-semibold">Emails to be sent</div>
                    <p className="mb-2 text-xs text-default-500">
                        Exactly what each person receives, merge fields resolved. Open a row to read theirs.
                        {c.stats.pending > c.pending_preview.length &&
                            ` Showing the first ${c.pending_preview.length} of ${c.stats.pending}.`}
                    </p>

                    <div className="flex flex-col gap-2">
                        {c.pending_preview.map((p) => (
                            <details key={p.email} className="rounded-xl border border-default-200 bg-content1">
                                <summary className="flex cursor-pointer flex-wrap items-center gap-2 px-4 py-3 text-sm">
                                    <span className="font-medium">{p.name || p.email}</span>
                                    <span className="text-default-400">{p.email}</span>
                                    <Chip size="sm" variant="flat" color={p.variant === 'stalled' ? 'warning' : 'default'}>
                                        {variantLabel(p.variant)}
                                    </Chip>
                                    {p.event && <span className="text-default-500">{p.event}</span>}
                                    {/* preventDefault stops the click from toggling the <details>. */}
                                    <button
                                        type="button"
                                        className="ml-auto rounded-lg border border-default-200 px-2 py-1 text-xs text-default-500 transition hover:border-danger hover:text-danger"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            skip.mutate({userUid: p.user_uid});
                                        }}
                                    >
                                        Don't email
                                    </button>
                                </summary>
                                <div className="border-t border-default-100 px-4 py-3">
                                    <div className="mb-2 text-sm">
                                        <span className="text-default-400">Subject: </span>
                                        <span className="font-medium">{p.subject}</span>
                                    </div>
                                    {/* Plain text is what actually sends, so show it as plain text. */}
                                    <pre className="max-h-80 overflow-auto whitespace-pre-wrap rounded-lg bg-default-50 p-3 font-sans text-sm leading-relaxed">
                                        {p.body}
                                    </pre>
                                </div>
                            </details>
                        ))}
                    </div>
                </div>
            )}

            {c.skipped.length > 0 && (
                <details className="rounded-xl border border-default-200 bg-content1 p-4">
                    <summary className="cursor-pointer text-sm font-semibold">
                        Excluded ({c.skipped.length}) — these people won't be emailed
                    </summary>
                    <div className="mt-3 flex flex-col gap-1.5">
                        {c.skipped.map((s) => (
                            <div key={s.user_uid} className="flex flex-wrap items-center gap-2 text-sm">
                                <span className="font-medium">{s.name || s.email}</span>
                                <span className="text-default-400">{s.email}</span>
                                <span className="text-xs text-default-400">excluded {s.skipped_at}</span>
                                <button
                                    type="button"
                                    className="ml-auto rounded-lg border border-default-200 px-2 py-1 text-xs text-default-500 transition hover:border-primary hover:text-primary"
                                    onClick={() => skip.mutate({userUid: s.user_uid, undo: true})}
                                >
                                    Put back
                                </button>
                            </div>
                        ))}
                    </div>
                </details>
            )}
        </div>
    );
}

/** The full outgoing-mail picture: everything the mailer accepted, not just campaigns. */
function EmailLog({log}: {log: Campaign['email_log']}) {
    return <>
        <p className="mb-3 text-sm text-default-500">
            Every email the platform sent, whatever triggered it — trial lifecycle, welcome links,
            password resets, campaigns. Latest 200, kept for 30 days. Campaign emails also
            track opens and clicks; this list just proves what went out, to whom, and when.
        </p>
        {log.length === 0 ? (
            <div className="rounded-xl border border-default-200 py-8 text-center text-default-500">
                Nothing logged yet — rows appear as emails go out from now on.
            </div>
        ) : (
            <div className="overflow-x-auto rounded-xl border border-default-200">
                <table className="w-full min-w-[720px] text-sm">
                    <thead>
                        <tr className="border-b border-default-200 bg-default-50 text-left text-xs uppercase tracking-wider text-default-500">
                            <th className="px-3 py-2">When</th>
                            <th className="px-3 py-2">Recipient</th>
                            <th className="px-3 py-2">Subject</th>
                        </tr>
                    </thead>
                    <tbody>
                        {log.map((e) => (
                            <tr key={e.id} className="border-b border-default-100 last:border-0">
                                <td className="whitespace-nowrap px-3 py-2 text-default-500">{e.at || '—'}</td>
                                <td className="whitespace-nowrap px-3 py-2 font-medium">{e.email}</td>
                                <td className="max-w-[420px] truncate px-3 py-2 text-default-600" title={e.subject || ''}>
                                    {e.subject || <span className="text-default-300">(no subject)</span>}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        )}
    </>;
}

/** What each tab is, in a line — shown under the tabs so the split explains itself. */
const TYPE_BLURB: Record<string, string> = {
    outreach: 'Emails you send by hand, to a chosen group. Open one to send it and see how it did.',
    features: 'Seven emails, one feature and one screenshot each, for organizers who already have an account, sent by hand, in order, a few days apart — and the Plus trial email, sent by hand to a filtered audience (listed while its switch on Master → Activation is on).',
    trial:    'Sent automatically around the free trial: the 2-day nudge, the come-back, the ending warning and the follow-ups.',
    billing:  'Sent automatically around payments: failed charges and upcoming renewals.',
    welcome:  'Sent automatically when someone signs up or creates an event.',
    other:    'Campaign keys in the send log that nobody has described yet.',
};

/** Every automatic email by trigger and timing, with its switch and wording (EmailJourneys). */
const JOURNEYS_TAB = 'journeys';
const LOG_TAB = 'log';

function RouteComponent() {
    const [tab, setTab] = useState(JOURNEYS_TAB);
    // The campaign whose stats are open; loaded on demand so the list stays quick.
    const [selected, setSelected] = useState<string | null>(null);
    const statsQuery = useQuery<CampaignStats>({
        queryKey: ['master-campaign-stats', selected],
        queryFn:  () => apiRetrieveImproved('master/campaigns/stats' as any, {campaign: selected}),
        enabled:  !!selected,
    });

    const query = useQuery<Campaign>({
        queryKey: ['master-campaigns'],
        queryFn:  () => apiRetrieveImproved('master/campaigns' as any, {}),
    });

    const c = query.data;
    if (!c) {
        return <><Header /><div className="flex justify-center py-12"><Spinner size="lg" /></div></>;
    }

    const campaigns = c.campaigns || [];
    // Every described type is a tab; "Other" only when something actually landed in it.
    const tabs = (c.types || []).filter((t) => t.key !== 'other' || campaigns.some((k) => k.type === 'other'));
    const inTab = campaigns.filter((k) => k.type === tab);

    const pick = (next: string) => {
        setTab(next);
        setSelected(null);
    };

    return <>
        <Header />
        <div className="mx-auto w-[98%] max-w-6xl py-4">
            <h1 className="text-2xl font-bold">Campaigns</h1>
            <p className="mb-3 text-sm text-default-500">Every email campaign, by type. Click one to see how it performed.</p>

            <div role="tablist" aria-label="Campaign types" className="mb-3 flex flex-wrap gap-1 border-b border-default-200">
                {[{key: JOURNEYS_TAB, label: 'Journeys'}, ...tabs, {key: LOG_TAB, label: 'All email'}].map((t) => {
                    const mine = campaigns.filter((k) => k.type === t.key);
                    // What is waiting on a person: only hand-sent campaigns have a queue to clear.
                    const waiting = mine.filter((k) => k.manual).reduce((n, k) => n + k.pending, 0);
                    return (
                        <button key={t.key} type="button" role="tab" aria-selected={tab === t.key} data-campaign-tab={t.key}
                                onClick={() => pick(t.key)}
                                className={'-mb-px flex items-center gap-1.5 rounded-t-lg border-b-2 px-4 py-2 text-sm font-semibold transition '
                                    + (tab === t.key ? 'border-primary text-primary' : 'border-transparent text-default-500 hover:text-default-800')}>
                            {t.label}
                            {t.key !== LOG_TAB && t.key !== JOURNEYS_TAB && <span className="text-xs font-normal text-default-400">{mine.length}</span>}
                            {waiting > 0 && <Chip size="sm" variant="flat" color="primary">{waiting} to send</Chip>}
                        </button>
                    );
                })}
            </div>

            {tab === JOURNEYS_TAB ? <EmailJourneys /> : tab === LOG_TAB ? <EmailLog log={c.email_log} /> : <>
                <p className="mb-3 text-sm text-default-500">{TYPE_BLURB[tab] || ''}</p>

                {/* The campaign list: one row each, click to open its stats (and console) underneath. */}
                <div className="mb-3 overflow-x-auto rounded-xl border border-default-200 bg-content1">
                    <table className="w-full min-w-[760px] text-sm">
                        <thead>
                            <tr className="border-b border-default-200 bg-default-50 text-left text-xs uppercase tracking-wider text-default-500">
                                <th className="px-3 py-2">Campaign</th>
                                <th className="px-3 py-2 text-right">Sent</th>
                                <th className="px-3 py-2 text-right">Opened</th>
                                <th className="px-3 py-2 text-right">Clicked</th>
                                <th className="px-3 py-2 text-right" title="Unsubscribed from this email">Unsub</th>
                                <th className="px-3 py-2 text-right">Failed</th>
                                <th className="px-3 py-2 text-right">To send</th>
                                <th className="px-3 py-2">Last sent</th>
                                <th className="px-3 py-2"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {inTab.length === 0 && (
                                <tr><td colSpan={9} className="px-3 py-8 text-center text-default-500">No campaigns of this type yet.</td></tr>
                            )}
                            {inTab.map((k) => (
                                <tr key={k.key} data-campaign={k.key}
                                    className={'cursor-pointer border-b border-default-100 transition last:border-0 hover:bg-default-50 ' + (selected === k.key ? 'bg-primary/5' : '')}
                                    onClick={() => setSelected(selected === k.key ? null : k.key)}>
                                    <td className="px-3 py-2.5">
                                        <div className="flex items-center gap-2 font-medium">
                                            {k.label}
                                            {/* Only campaigns with a master switch say so; the rest simply run. */}
                                            {k.switch && <Chip size="sm" variant="flat" color={k.switch.on ? 'success' : 'default'}>{k.switch.on ? 'Live' : 'Off'}</Chip>}
                                            {k.manual && <Chip size="sm" variant="flat" color="secondary">Sent by hand</Chip>}
                                        </div>
                                        {k.description && <div className="max-w-[420px] truncate text-xs text-default-400" title={k.description}>{k.description}</div>}
                                    </td>
                                    <td className="px-3 py-2.5 text-right tabular-nums">{k.sent}</td>
                                    <td className="px-3 py-2.5 text-right tabular-nums">{k.opened} <span className="text-xs text-default-400">{k.open_rate === null ? '' : `(${k.open_rate}%)`}</span></td>
                                    <td className="px-3 py-2.5 text-right tabular-nums">{k.clicked} <span className="text-xs text-default-400">{k.click_rate === null ? '' : `(${k.click_rate}%)`}</span></td>
                                    <td className="px-3 py-2.5 text-right tabular-nums">{k.unsubscribed || <span className="text-default-300">0</span>}</td>
                                    <td className="px-3 py-2.5 text-right tabular-nums">{k.failed}</td>
                                    <td className="px-3 py-2.5 text-right tabular-nums">{k.pending}</td>
                                    <td className="whitespace-nowrap px-3 py-2.5 text-default-500">{k.last_sent_at || '—'}</td>
                                    <td className="whitespace-nowrap px-3 py-2.5 text-right text-xs text-primary">
                                        {selected === k.key ? 'Hide' : k.manual ? 'Send & stats' : 'View stats'}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {selected && (
                    <div className="mb-8">
                        {statsQuery.isLoading || !statsQuery.data
                            ? <div className="flex justify-center py-8"><Spinner size="sm" /></div>
                            : (
                                <CampaignDetail stats={statsQuery.data}>
                                    {/* Keyed so opening another email never shows the last one's people. */}
                                    {(selected === 'founder_feedback' || selected.startsWith('feature_')) && <FeedbackConsole key={selected} campaign={selected} />}
                                    {selected === 'organizer_winback' && <WinbackConsole c={c} />}
                                    {selected === 'plus_trial_pitch' && (
                                        <AudienceConsole key={selected} campaign="plus_trial_pitch" dataPath="master/campaigns/plus-trial" sendPath="master/campaigns/plus-trial/send"
                                                         blurb="The signup send already emails every new club a little after it is created; this is for everyone it missed." />
                                    )}
                                    {selected === 'pass_offer' && (
                                        <AudienceConsole key={selected} campaign="pass_offer" dataPath="master/campaigns/pass-offer" sendPath="master/campaigns/pass-offer/send" seenModal
                                                         blurb="The automatic send offers every club 6 hours after it first sees the pass modal; this is for everyone it missed. Each club gets one code, ever, good for 72 hours." />
                                    )}
                                    {selected === 'free_events_pitch' && (
                                        <AudienceConsole key={selected} campaign="free_events_pitch" dataPath="master/campaigns/free-events" sendPath="master/campaigns/free-events/send"
                                                         seenModal seenLabel="Has seen the Event Pass"
                                                         blurb="Shaun's note that every league and tournament is now free, whatever the size, to organisers who were shown the Event Pass (banner or modal) and never bought one. Nothing goes by itself; each club gets it once, ever." />
                                    )}
                                </CampaignDetail>
                            )}
                    </div>
                )}
            </>}
        </div>
    </>;
}
